"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { DEFAULT_HERO_MUSIC_VISUAL_CONTROLS, HERO_MUSIC_VISUAL_LAYOUT_STYLE } from "./HeroMusicVisualControls";
import { getHeroMusicSkeletonCoverPaths } from "./HeroMusicVisualSkeletonGeometry";

type HeroMusicVisualSkeletonProps = {
  coverCount?: number;
};

type SkeletonSize = {
  height: number;
  width: number;
};

const DEFAULT_SKELETON_COVER_COUNT = 8;
const DEFAULT_SKELETON_SIZE = {
  height: DEFAULT_HERO_MUSIC_VISUAL_CONTROLS.height.desktop,
  width: DEFAULT_HERO_MUSIC_VISUAL_CONTROLS.maxContainerWidth,
} satisfies SkeletonSize;

export function HeroMusicVisualSkeleton({ coverCount = DEFAULT_SKELETON_COVER_COUNT }: HeroMusicVisualSkeletonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<SkeletonSize>(DEFAULT_SKELETON_SIZE);
  const coverPaths = useMemo(
    () =>
      getHeroMusicSkeletonCoverPaths({
        controls: DEFAULT_HERO_MUSIC_VISUAL_CONTROLS,
        coverCount,
        height: size.height,
        width: size.width,
      }),
    [coverCount, size.height, size.width],
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const containerElement = container;

    function updateSize() {
      setSize(currentSize => {
        const nextSize = getElementSize(containerElement);

        if (currentSize.width === nextSize.width && currentSize.height === nextSize.height) return currentSize;

        return nextSize;
      });
    }

    updateSize();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateSize);

    observer.observe(containerElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden='true'
      className='tw-pointer-events-none tw-relative tw-mx-auto tw-h-full tw-w-full tw-overflow-hidden'
      inert
      style={{
        ...HERO_MUSIC_VISUAL_LAYOUT_STYLE,
        maxWidth: `${DEFAULT_HERO_MUSIC_VISUAL_CONTROLS.maxContainerWidth}px`,
      }}
    >
      <svg
        className='tw-absolute tw-inset-0 tw-h-full tw-w-full'
        fill={DEFAULT_HERO_MUSIC_VISUAL_CONTROLS.coverBackfaceColor}
        height={size.height}
        preserveAspectRatio='none'
        viewBox={`0 0 ${size.width} ${size.height}`}
        width={size.width}
        xmlns='http://www.w3.org/2000/svg'
      >
        {coverPaths.map(path => (
          <path d={path.d} key={path.positionIndex} />
        ))}
      </svg>
    </div>
  );
}

function getElementSize(element: HTMLDivElement) {
  const width = element.clientWidth || DEFAULT_SKELETON_SIZE.width;
  const height = element.clientHeight || DEFAULT_SKELETON_SIZE.height;

  return { height, width } satisfies SkeletonSize;
}
